import { getCookie } from '@helpers';
import { setDefaultOptions } from 'date-fns';
import { ja } from 'date-fns/locale';
import i18next, { ReadCallback } from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

// Mirrors the cookie key used by `useLanguageStore`. Kept inline (rather than
// imported from the store) so this module has zero app-internal imports and
// is always safe to evaluate first — Rolldown's chunk ordering can otherwise
// race `useLanguageStore`'s init-time `changeLanguage()` call.
const NEXT_LOCALE_COOKIE_KEY = 'NEXT_LOCALE';

const detectInitialLanguage = (): string => {
  const fromCookie = getCookie(NEXT_LOCALE_COOKIE_KEY);
  if (typeof fromCookie === 'string') {
    return fromCookie;
  }
  return window.navigator.language.substring(0, 2) || 'en';
};

const initialLng = detectInitialLanguage();

setDefaultOptions({ locale: initialLng === 'ja' ? ja : undefined });

const localeLoaders = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../../static/locales/*/*.json'
);

const localePath = (language: string, namespace: string) =>
  `../../../static/locales/${language}/${namespace}.json`;

i18next
  .use(initReactI18next)
  .use(
    resourcesToBackend(
      (language: string, namespace: string, callback: ReadCallback) => {
        const load = localeLoaders[localePath(language, namespace)];

        if (!load) {
          callback(
            new Error(`i18n: missing locale ${language}/${namespace}`),
            null
          );
          return;
        }

        load()
          .then(({ default: resources }) => {
            callback(null, resources);
          })
          .catch(error => {
            callback(error, null);
          });
      }
    )
  )
  .init({
    fallbackLng: 'en',
    lng: initialLng,
    debug: false,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      useSuspense: true,
    },
  });

export { i18next as i18n };
